import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Paper, Button, Chip, LinearProgress,
  Card, CardContent, CardActions, Divider, Alert, CircularProgress,
  MenuItem, Select, FormControl, InputLabel, Tooltip, IconButton,
  Stack, Grid
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Description as DocIcon,
  CheckCircle as VerifiedIcon,
  Cancel as RejectedIcon,
  HourglassEmpty as PendingIcon,
  ArrowBack as BackIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { parentAPI } from '../../services/api';
import EmptyState from '../../components/EmptyState';

const MAX_FILE_BYTES = 5 * 1024 * 1024;
const MAX_DOCS = 5;
const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
const ALLOWED_EXT_LABEL = 'PDF, JPG, PNG';

const DOC_TYPE_LABELS = {
  birth_certificate: 'Birth Certificate',
  transfer_certificate: 'Transfer Certificate',
  aadhaar: 'Aadhaar',
  photo: 'Photo',
  report_card: 'Report Card',
  other: 'Other',
};

const STATUS_CONFIG = {
  submitted: { label: 'Submitted',  color: '#e3f2fd', textColor: '#1565c0', Icon: PendingIcon },
  verified:  { label: 'Verified',   color: '#e8f5e9', textColor: '#2e7d32', Icon: VerifiedIcon },
  rejected:  { label: 'Rejected',   color: '#ffebee', textColor: '#c62828', Icon: RejectedIcon },
};

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function FileTypeIcon({ mime }) {
  if (mime === 'application/pdf') return <PdfIcon sx={{ color: '#e53935' }} />;
  if (mime.startsWith('image/')) return <ImageIcon sx={{ color: '#1976d2' }} />;
  return <FileIcon />;
}

export default function ChildDocuments() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [documents, setDocuments] = useState([]);
  const [meta, setMeta] = useState({ count: 0, limit: MAX_DOCS, remaining: MAX_DOCS });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [alert, setAlert] = useState(null);

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fileErrors, setFileErrors] = useState([]);
  const [docType, setDocType] = useState('other');

  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await parentAPI.documents.list(studentId);
      setDocuments(data.documents || []);
      setMeta(data.meta || { count: 0, limit: MAX_DOCS, remaining: MAX_DOCS });
    } catch (err) {
      setAlert({ severity: 'error', message: 'Failed to load documents.' });
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => { load(); }, [load]);

  function validateFiles(files) {
    const errors = [];
    for (const f of files) {
      if (!ALLOWED_TYPES.includes(f.type)) {
        errors.push(`"${f.name}": only ${ALLOWED_EXT_LABEL} allowed.`);
      } else if (f.size > MAX_FILE_BYTES) {
        errors.push(`"${f.name}": exceeds 5 MB limit (${formatBytes(f.size)}).`);
      }
    }
    return errors;
  }

  function handleFileSelect(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = '';

    const available = meta.remaining;
    if (files.length > available) {
      setAlert({ severity: 'warning', message: `You can only add ${available} more document${available !== 1 ? 's' : ''}. Selected ${files.length}.` });
      return;
    }

    const errors = validateFiles(files);
    setFileErrors(errors);
    if (errors.length === 0) {
      setSelectedFiles(files);
      setAlert(null);
    }
  }

  async function handleUpload() {
    if (selectedFiles.length === 0) return;

    const errors = validateFiles(selectedFiles);
    if (errors.length > 0) {
      setFileErrors(errors);
      return;
    }

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('document_type', docType);
      for (const f of selectedFiles) {
        fd.append('documents', f);
      }
      const { data } = await parentAPI.documents.upload(studentId, fd);
      setDocuments(prev => [...(data.documents || []), ...prev]);
      setMeta(data.meta);
      setSelectedFiles([]);
      setDocType('other');
      setFileErrors([]);
      setAlert({ severity: 'success', message: `${data.documents.length} document${data.documents.length !== 1 ? 's' : ''} uploaded successfully.` });
    } catch (err) {
      const msg = err.response?.data?.message || 'Upload failed. Please try again.';
      setAlert({ severity: 'error', message: msg });
    } finally {
      setUploading(false);
    }
  }

  function triggerDownload(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function handleDownload(doc) {
    try {
      const { data } = await parentAPI.documents.download(studentId, doc.id);
      triggerDownload(data, doc.original_name);
    } catch {
      setAlert({ severity: 'error', message: 'Download failed.' });
    }
  }

  const usagePercent = Math.round((meta.count / MAX_DOCS) * 100);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f0f4f8' }}>
      <Container maxWidth="md" sx={{ py: 4 }}>
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={1} mb={1}>
          <IconButton onClick={() => navigate(-1)} size="small">
            <BackIcon />
          </IconButton>
          <Typography variant="h5" fontWeight={700} color="#1a3c5e">
            Student Documents
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Upload onboarding documents for your child. Supported: {ALLOWED_EXT_LABEL} · Max 5 MB per file.
        </Typography>

        {alert && (
          <Alert severity={alert.severity} onClose={() => setAlert(null)} sx={{ mb: 2 }}>
            {alert.message}
          </Alert>
        )}

        {/* Usage bar */}
        <Paper sx={{ p: 2.5, mb: 3 }} elevation={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle2" fontWeight={700}>
              Storage Usage
            </Typography>
            <Typography variant="subtitle2" fontWeight={700} color={meta.count >= MAX_DOCS ? 'error.main' : 'text.primary'}>
              {meta.count} / {MAX_DOCS} documents used
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={usagePercent}
            sx={{
              height: 8, borderRadius: 4,
              bgcolor: '#e0e0e0',
              '& .MuiLinearProgress-bar': { bgcolor: meta.count >= MAX_DOCS ? '#e53935' : '#00796b' }
            }}
          />
          {meta.count >= MAX_DOCS && (
            <Typography variant="caption" color="error" mt={0.5} display="block">
              Document limit reached. To remove a document, contact your school.
            </Typography>
          )}
        </Paper>

        {/* Upload panel */}
        {meta.remaining > 0 && (
          <Paper sx={{ p: 3, mb: 3 }} elevation={2}>
            <Typography variant="subtitle1" fontWeight={700} mb={2}>
              Upload Documents
            </Typography>
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} sm={5}>
                <FormControl fullWidth size="small">
                  <InputLabel>Document Type</InputLabel>
                  <Select
                    value={docType}
                    label="Document Type"
                    onChange={e => setDocType(e.target.value)}
                  >
                    {Object.entries(DOC_TYPE_LABELS).map(([v, l]) => (
                      <MenuItem key={v} value={v}>{l}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={7}>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png"
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                />
                <Button
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  fullWidth
                >
                  Choose Files (up to {meta.remaining})
                </Button>
              </Grid>
            </Grid>

            {fileErrors.length > 0 && (
              <Box mt={1.5}>
                {fileErrors.map((e, i) => (
                  <Alert key={i} severity="error" sx={{ mb: 0.5, py: 0 }}>{e}</Alert>
                ))}
              </Box>
            )}

            {selectedFiles.length > 0 && fileErrors.length === 0 && (
              <Box mt={1.5}>
                <Typography variant="caption" color="text.secondary">
                  Selected: {selectedFiles.map(f => f.name).join(', ')}
                </Typography>
                <Box mt={1.5} display="flex" justifyContent="flex-end">
                  <Button
                    variant="contained"
                    onClick={handleUpload}
                    disabled={uploading}
                    startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
                    sx={{ bgcolor: '#00796b', '&:hover': { bgcolor: '#004d40' } }}
                  >
                    {uploading ? 'Uploading…' : 'Upload'}
                  </Button>
                </Box>
              </Box>
            )}
          </Paper>
        )}

        {/* Document list */}
        <Typography variant="subtitle1" fontWeight={700} mb={1.5}>
          Uploaded Documents
        </Typography>

        {loading ? (
          <Box display="flex" justifyContent="center" py={5}>
            <CircularProgress />
          </Box>
        ) : documents.length === 0 ? (
          <EmptyState
            icon={DocIcon}
            title="No documents uploaded yet."
          />
        ) : (
          <Stack spacing={1.5}>
            {documents.map(doc => {
              const sc = STATUS_CONFIG[doc.status] || STATUS_CONFIG.submitted;
              return (
                <Card key={doc.id} elevation={2}>
                  <CardContent sx={{ pb: 1 }}>
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                      <FileTypeIcon mime={doc.mime_type} />
                      <Box flex={1} minWidth={0}>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          noWrap
                          title={doc.original_name}
                        >
                          {doc.original_name}
                        </Typography>
                        <Stack direction="row" spacing={1} mt={0.5} flexWrap="wrap" gap={0.5}>
                          <Chip
                            label={DOC_TYPE_LABELS[doc.document_type] || doc.document_type}
                            size="small"
                            sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontSize: '0.7rem' }}
                          />
                          <Chip
                            icon={<sc.Icon sx={{ fontSize: '0.85rem !important' }} />}
                            label={sc.label}
                            size="small"
                            sx={{ bgcolor: sc.color, color: sc.textColor, fontSize: '0.7rem' }}
                          />
                          <Typography variant="caption" color="text.secondary" alignSelf="center">
                            {formatBytes(doc.file_size)} · {new Date(doc.created_at).toLocaleDateString('en-IN')}
                          </Typography>
                        </Stack>
                      </Box>
                    </Stack>
                  </CardContent>
                  <Divider />
                  <CardActions sx={{ px: 2, py: 0.75, justifyContent: 'flex-end' }}>
                    <Button
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleDownload(doc)}
                      sx={{ color: '#00796b' }}
                    >
                      Download
                    </Button>
                  </CardActions>
                </Card>
              );
            })}
          </Stack>
        )}

        <Typography variant="caption" color="text.secondary" display="block" mt={3} textAlign="center">
          To remove a document, please contact your school office.
        </Typography>
      </Container>
    </Box>
  );
}
